import { modK } from '../lib/platform'
import { useCallback, useMemo, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { addDays, type ISODate, type JournalEntry } from '@app/shared'

import { useClock, useJournalEntries, useJournalStats } from '../lib/data'
import { DataModeBadge, HeaderBand } from '../components/HeaderBand'
import { Search } from '../components/icons'
import { Plus } from '../components/journal/icons'
import { DayCard, DayCardSkeleton, NoEntryCard } from '../components/journal/DayCards'
import {
  FilterRow,
  MonthPicker,
  NO_FILTERS,
  OnThisDay,
  QuietPattern,
  StatsPanel,
  anyFilterOn,
  type Filters,
} from '../components/journal/IndexPanels'

/**
 * JOURNAL INDEX — docs/design/handoff/"LifeOS Journal.dc.html", option 5a.
 *
 * The archive. Stats and a 26-week heatmap up top, filter chips, then day cards in reverse
 * chronological order — and THE GAPS ARE THE POINT: a day with no entry renders a visible "no
 * entry" card rather than being skipped (README §2). On-this-day sits in the right column.
 *
 * THE WINDOW IS A MONTH. The mockup's chip reads "August 2026" and its count reads "Showing 24
 * days", so the list is a date range and not a page of the last N entries. A month is the range
 * the month picker and the coverage line ("14 of 17 days written") already speak in, so the list
 * speaks in it too — one window, three controls, no second mental model.
 */
export function Journal() {
  const { data: clock } = useClock()
  const { data: stats, error: statsError, isPending: statsPending } = useJournalStats()

  /**
   * THE TAG FILTER LIVES IN THE URL, the other two do not.
   *
   * `?tag=` is how the day page's Threads card says "see all of this thread", so it has to survive
   * a navigation and a reload — a "See all" link that lands on an unfiltered archive is a link
   * that lies. `Has voice` and the mood range are throwaway view state and stay local.
   */
  const [params, setParams] = useSearchParams()
  const [localFilters, setLocalFilters] = useState<Filters>(NO_FILTERS)
  const filters: Filters = useMemo(
    () => ({ ...localFilters, tags: params.getAll('tag') }),
    [localFilters, params],
  )

  const setFilters = useCallback(
    (next: Filters) => {
      setLocalFilters({ ...next, tags: [] })
      const updated = new URLSearchParams(params)
      updated.delete('tag')
      for (const tag of next.tags) updated.append('tag', tag)
      setParams(updated, { replace: true })
    },
    [params, setParams],
  )

  // The month lives in the URL for the same reason: it is the window the whole screen is about,
  // and a picked month that a reload throws away is a screen you cannot send anyone.
  const month = params.get('month') ?? clock?.date.slice(0, 7) ?? null
  const setMonth = useCallback(
    (next: string) => {
      const updated = new URLSearchParams(params)
      updated.set('month', next)
      setParams(updated, { replace: true })
    },
    [params, setParams],
  )

  // The range for the month, clamped at today: the archive never renders days that have not
  // happened, because a future date with no entry is not a day anybody failed to write.
  const range = useMemo(() => {
    if (!month || !clock) return undefined
    const from = `${month}-01` as ISODate
    const lastOfMonth = new Date(
      Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0, 12),
    )
      .toISOString()
      .slice(0, 10) as ISODate
    return { from, to: (lastOfMonth < clock.date ? lastOfMonth : clock.date) as ISODate }
  }, [month, clock])

  const { data: entries, error: entriesError, isPending: entriesPending } = useJournalEntries(range)

  const rows = useMemo(() => buildRows(entries, range, filters), [entries, range, filters])
  const filtered = anyFilterOn(filters)
  const showing = rows
    ? `Showing ${rows.length} ${filtered ? (rows.length === 1 ? 'entry' : 'entries') : rows.length === 1 ? 'day' : 'days'} · newest first`
    : ''

  return (
    <>
      <HeaderBand band="journal" tall>
        <div className="flex flex-none items-center gap-3 whitespace-nowrap">
          <span className="text-[12px] leading-none text-muted">Journal</span>
          <span className="text-[12px] leading-none text-faintest" aria-hidden>
            /
          </span>
          <span className="text-[12px] font-medium leading-none text-ink-2">All entries</span>
        </div>

        <JournalSearch />
        <DataModeBadge />
        {clock && (
          <Link
            to="/"
            className="flex flex-none items-center gap-[9px] rounded-button bg-ink px-[14px] py-2 transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
          >
            <Plus size={14} strokeWidth={1.8} className="text-today-lift" />
            <span className="text-[12px] font-semibold leading-none text-surface">Write today</span>
          </Link>
        )}
      </HeaderBand>

      <div className="flex min-h-0 flex-1 flex-col gap-[14px] px-[30px] pb-[26px]">
        <StatsPanel stats={stats} error={statsError} isPending={statsPending} />

        <FilterRow
          stats={stats}
          filters={filters}
          onChange={setFilters}
          month={month ?? ''}
          onMonth={setMonth}
          showing={showing}
        />

        <div className="flex min-h-0 flex-1 gap-[14px]">
          <div className="flex min-w-0 flex-1 flex-col gap-[10px] overflow-y-auto pr-1">
            {entriesError ? (
              // The archive read is the one thing this screen cannot degrade around, so it says
              // what broke, what still works, and offers exactly one way forward.
              <section className="rounded-card border border-line bg-surface px-[18px] py-4 shadow-card">
                <p className="text-body-sm text-ink-3 text-pretty">
                  {entriesError instanceof Error
                    ? entriesError.message
                    : 'The archive could not be read.'}
                </p>
                <p className="mt-2 text-[11.5px] leading-[1.5] text-microcopy text-pretty">
                  Nothing is lost — every entry is still on the server, and a single day still opens
                  from its own address.
                </p>
                {clock && (
                  <Link
                    to={`/journal/${clock.date}`}
                    className="mt-3 inline-block rounded-button bg-ink px-3 py-[7px] text-[11.5px] font-semibold leading-none text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
                  >
                    Open today
                  </Link>
                )}
              </section>
            ) : entriesPending || !rows || !clock ? (
              <>
                <DayCardSkeleton />
                <DayCardSkeleton />
                <DayCardSkeleton />
              </>
            ) : rows.length === 0 ? (
              <p className="px-[18px] py-4 text-body-sm text-microcopy text-pretty">
                {filtered
                  ? 'No entry this month matches those filters. Turn one off to widen it.'
                  : 'Nothing was written this month.'}
              </p>
            ) : (
              rows.map((row) =>
                row.entry ? (
                  <DayCard key={row.date} entry={row.entry} isToday={row.date === clock.date} />
                ) : (
                  <NoEntryCard key={row.date} date={row.date} />
                ),
              )
            )}
          </div>

          <aside className="flex w-[296px] flex-none flex-col gap-3 overflow-y-auto">
            {clock && <OnThisDay from={clock.date} />}
            <MonthPicker stats={stats} month={month ?? ''} onMonth={setMonth} />
            <QuietPattern stats={stats} />
          </aside>
        </div>
      </div>
    </>
  )
}

interface Row {
  date: ISODate
  entry: JournalEntry | null
}

/**
 * One row per DAY of the window, newest first — the "no entry" gaps included.
 *
 * WITH A FILTER ON, THE GAPS GO AWAY. A dashed "no entry" card next to an active #cogsci175 chip
 * would claim the owner wrote nothing that day, when the truth is that what he wrote does not
 * carry that tag. So a filtered list is a list of matching ENTRIES, and the count line switches
 * word to match.
 */
function buildRows(
  entries: JournalEntry[] | undefined,
  range: { from: ISODate; to: ISODate } | undefined,
  filters: Filters,
): Row[] | undefined {
  if (!entries || !range) return undefined

  const matches = (e: JournalEntry) =>
    (!filters.hasVoice || e.voiceMemoCount > 0) &&
    (filters.tags.length === 0 || filters.tags.some((t) => e.tags.includes(t))) &&
    (filters.moodAtLeast === null || (e.mood !== null && e.mood >= filters.moodAtLeast))

  const kept = entries.filter(matches)
  if (anyFilterOn(filters)) {
    return [...kept]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((entry) => ({ date: entry.date, entry }))
  }

  const byDate = new Map(kept.map((e) => [e.date, e]))
  const rows: Row[] = []
  for (let d = range.to; d >= range.from; d = addDays(d, -1)) {
    rows.push({ date: d, entry: byDate.get(d) ?? null })
  }
  return rows
}

/**
 * The band's search field, scoped to the journal — the mockup's placeholder is "Search your
 * journal only", and the scope is carried into the URL so `/search` cannot silently widen it.
 */
function JournalSearch() {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        const q = query.trim()
        navigate(`/search?scope=journal${q ? `&q=${encodeURIComponent(q)}` : ''}`)
      }}
      style={{ '--mo-rest-shadow': 'var(--shadow-field)' } as CSSProperties}
      className="mo-field flex min-w-0 max-w-[400px] flex-1 items-center gap-[10px] rounded-tile border border-line-2 bg-surface/[0.86] px-[14px] py-[10px] shadow-field"
    >
      <Search size={15} strokeWidth={1.8} className="flex-none text-microcopy" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search your journal only"
        aria-label="Search your journal only"
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] leading-none text-ink outline-none placeholder:text-muted"
      />
      <kbd className="flex-none font-mono text-[10.5px] font-medium leading-none text-microcopy">
        {modK}
      </kbd>
    </form>
  )
}
